import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ActionTypes } from '../context/AppContext';

function CompanyAgent() {
  const { state, dispatch } = useApp();
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'system',
      content: '👋 Welcome to your Company Agent! Ask me about our game vision, development roadmap, technologies, or any company documentation. I can search through our knowledge base to answer your questions.',
      timestamp: new Date().toLocaleTimeString(),
      isWelcome: true
    }
  ]);
  const chatEndRef = useRef(null);

  // Generate session ID on component mount
  useEffect(() => {
    const generateSessionId = () => {
      const timestamp = Math.floor(Date.now() / 1000);
      const randomId = Math.random().toString(36).substring(2, 10);
      return `rag_rahul_${timestamp}_${randomId}`;
    };
    
    setSessionId(generateSessionId());
  }, []);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Store the query before clearing it
    const currentQuery = query;
    
    // Add user message to chat history with context info
    let messageContent = currentQuery;
    if (selectedPerson) {
      messageContent = `${currentQuery}\n\n👤 Person: ${selectedPerson}`;
    }
    
    const userMessage = {
      type: 'user',
      content: messageContent,
      timestamp: new Date().toLocaleTimeString(),
      hasContext: !!selectedPerson
    };
    
    // Add loading message
    const loadingMessage = {
      type: 'ai',
      content: 'Processing your company query...',
      timestamp: new Date().toLocaleTimeString(),
      isLoading: true
    };
    
    setChatHistory(prev => [...prev, userMessage, loadingMessage]);
    setQuery(''); // Clear input immediately

    try {
      // Company RAG API endpoint request
      const requestBody = {
        question: currentQuery,
        user_id: "Rahul",
        session_id: sessionId
      };

      // Make the actual API call to Company RAG system
      const response = await fetch('https://langraphdev-development.up.railway.app/api/hey-mission/rag/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });

      // Get response as text first to see what we're getting
      const responseText = await response.text();

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        // Check if it's an HTML error page
        if (responseText.includes('<!DOCTYPE') || responseText.includes('<html>')) {
          responseData = { error: 'Server returned HTML instead of JSON - likely a CORS or server error' };
        } else {
          responseData = { 
            error: 'Invalid response format', 
            rawResponse: responseText.substring(0, 500) + '...' 
          };
        }
      }

      if (response.ok && responseData && !responseData.error) {
        // Extract and display the RAG response
        const aiResponse = responseData.answer || responseData.response || responseData.message || 'Response received successfully.';
        const sources = responseData.sources || [];
        const responseTime = responseData.response_time_ms || responseData.latency_ms;
        const sessionInfo = responseData.session_id;

        // Remove loading message and add AI response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          // Create AI response message
          const companyResponse = {
            type: 'ai',
            content: aiResponse,
            timestamp: new Date().toLocaleTimeString(),
            isSuccess: true,
            metadata: {
              responseTime: responseTime ? Math.round(responseTime) : null,
              sessionId: sessionInfo,
              queryType: 'company-rag',
              sources: sources.length > 0 ? sources : null
            }
          };
          
          return [...newHistory, companyResponse];
        });
      } else {
        const errorMsg = responseData?.error || `HTTP ${response.status}: ${response.statusText}`;
        
        // Remove loading message and add error response
        setChatHistory(prev => {
          const newHistory = prev.filter(msg => !msg.isLoading);
          
          const errorResponse = {
            type: 'ai',
            content: `❌ Company query failed: ${errorMsg}`,
            timestamp: new Date().toLocaleTimeString(),
            isError: true
          };
          
          return [...newHistory, errorResponse];
        });
      }

    } catch (error) {
      // Remove loading message and add error response
      setChatHistory(prev => {
        const newHistory = prev.filter(msg => !msg.isLoading);
        
        const errorResponse = {
          type: 'ai',
          content: `❌ Network error: ${error.message}`,
          timestamp: new Date().toLocaleTimeString(),
          isError: true
        };
        
        return [...newHistory, errorResponse];
      });
    }

    setIsSubmitting(false);
  };

  const goBackToDashboard = () => {
    dispatch({ type: ActionTypes.SET_SCREEN, payload: 'dashboard' });
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-4xl mx-auto h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Company Agent</h1>
        <button 
          onClick={goBackToDashboard}
          className="text-sm text-gray-400 hover:text-white"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Context Dropdown */}
      <div className="mb-4 p-4 bg-gray-900/30 rounded-lg border border-gray-700">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-400 mb-2">
            Select Person for Context (Optional)
          </label>
          <select
            value={selectedPerson}
            onChange={(e) => setSelectedPerson(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-sm text-white focus:border-gray-500 focus:outline-none"
          >
            <option value="">No person selected</option>
            {Object.keys(state.userProfiles || {}).map((personName) => (
              <option key={personName} value={personName}>
                {personName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-gray-900/50 rounded-lg p-4 overflow-y-auto mb-4 space-y-3">
        {chatHistory.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              message.type === 'user' 
                ? 'bg-gray-700 text-white border border-gray-600' 
                : message.isError
                ? 'bg-red-900/50 text-red-200 border border-red-500'
                : message.isSuccess
                ? 'bg-gray-600 text-white border border-gray-500'
                : message.isWelcome
                ? 'bg-black text-gray-200 border border-gray-600'
                : 'bg-gray-700 text-gray-200 border border-gray-600'
            }`}>
              <div className="text-sm">
                {message.isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    {message.content}
                  </div>
                ) : (
                  <>
                    {message.content}
                    
                    {/* Show metadata for successful AI responses */}
                    {message.metadata && (
                      <div className="mt-3 pt-3 border-t border-gray-600/50 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                          {message.metadata.responseTime && (
                            <div><strong>Response Time:</strong> {message.metadata.responseTime}ms</div>
                          )}
                          {message.metadata.sessionId && (
                            <div><strong>Session:</strong> {message.metadata.sessionId.split('_').pop()}</div>
                          )}
                          <div><strong>Type:</strong> Company RAG</div>
                          <div><strong>Status:</strong> Success</div>
                        </div>
                        
                        {/* Show sources if available */}
                        {message.metadata.sources && message.metadata.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-600/30">
                            <div className="text-xs text-gray-400 mb-1">
                              <strong>📚 Sources ({message.metadata.sources.length}):</strong>
                            </div>
                            <div className="space-y-1">
                              {message.metadata.sources.slice(0, 3).map((source, idx) => (
                                <div key={idx} className="text-xs text-blue-300 truncate">
                                  • {source.title || source.name || source.document || `Source ${idx + 1}`}
                                </div>
                              ))}
                              {message.metadata.sources.length > 3 && (
                                <div className="text-xs text-gray-500">
                                  ... and {message.metadata.sources.length - 3} more sources
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {message.timestamp}
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask about game vision, roadmap, technologies, documentation..."
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm"
          disabled={isSubmitting}
        />
        <button
          type="submit"
          disabled={isSubmitting || !query.trim()}
          className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 flex items-center gap-2 border border-gray-600"
        >
          {isSubmitting ? (
            <>
              <div className="spinner w-4 h-4"></div>
              Send
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Send
            </>
          )}
        </button>
      </form>

      {/* Context Status */}
      {selectedPerson && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-4">
          <span>📌 <strong>Context:</strong></span>
          <span className="text-white">👤 {selectedPerson}</span>
        </div>
      )}

      {/* Example Prompts */}
      <div className="mt-3 text-xs text-gray-500">
        <p className="mb-1">💡 <strong>Example prompts:</strong></p>
        <div className="space-y-1 text-gray-400">
          <p>• "What is the vision of the game?"</p>
          <p>• "What are the core gameplay mechanics we're building?"</p>
          <p>• "What's our development roadmap and milestones?"</p>
          <p>• "What technologies and tools are we using for development?"</p>
        </div>
      </div>
    </div>
  );
}

export default CompanyAgent;
